import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type {
  CreateLeadLabelInput,
  LabelLeadCountsResponse,
  LeadLabel,
  LeadLabelWithUsage,
  UpdateLeadLabelInput,
} from "@school-sales-ai/shared";
import { api } from "@/lib/api";
import { buildQueryString } from "@/lib/query-string";
import {
  assignLabelApi,
  createLabelApi,
  deleteLabelApi,
  listLabelsApi,
  unassignLabelApi,
  updateLabelApi,
} from "@/lib/api";

const LABELS_KEY = ["labels"];
const LABEL_USAGE_KEY = ["label-usage"];

/**
 * Active-lead counts per label for the filter dropdown. Contextual: pass the
 * other active filters (never `labelIds`) so counts stay predictive, mirroring
 * `useProductLeadCounts`.
 */
export type LabelCountFilters = {
  // Lists are comma-joined.
  stages?: string;
  channel?: string;
  scoreMin?: string;
  scoreMax?: string;
  productIds?: string;
  cycleIds?: string;
  search?: string;
  from?: string;
  to?: string;
  assigneeIds?: string;
};

export function useLabelLeadCounts(
  filters: LabelCountFilters,
): ReturnType<typeof useQuery<LabelLeadCountsResponse>> {
  const clean = Object.fromEntries(
    Object.entries(filters).filter(([, v]) => v !== "" && v !== undefined),
  ) as Record<string, string>;
  const qs = buildQueryString(clean);
  return useQuery<LabelLeadCountsResponse>({
    queryKey: ["label-counts", filters],
    queryFn: () =>
      api.get<LabelLeadCountsResponse>(
        `/crm/leads/label-counts${qs ? `?${qs}` : ""}`,
      ),
    staleTime: 60_000,
  });
}

export function useLabelsQuery(): ReturnType<
  typeof useQuery<LeadLabelWithUsage[]>
> {
  return useQuery<LeadLabelWithUsage[]>({
    queryKey: LABELS_KEY,
    queryFn: listLabelsApi,
  });
}

/**
 * The same endpoint, on its own key, for the manage-labels modal.
 *
 * Two keys rather than one because `assignedCount` and the chips have opposite
 * freshness needs. The modal disables "Eliminar" on a label in use, so a stale
 * count locks a deletable label — it must refetch every time it opens. But
 * `["labels"]` feeds every chip on a 478-card board through one context value,
 * and refetching it mints a new array that re-renders all of them; that is the
 * cost `usePatchLeadLabels` exists to avoid. So assigning keeps patching the
 * lead caches and leaves `["labels"]` alone, and this key — inactive while the
 * modal is closed, so invalidating it fetches nothing — carries the counts.
 */
export function useLabelUsageQuery(
  enabled: boolean,
): ReturnType<typeof useQuery<LeadLabelWithUsage[]>> {
  return useQuery<LeadLabelWithUsage[]>({
    queryKey: LABEL_USAGE_KEY,
    queryFn: listLabelsApi,
    enabled,
    staleTime: 0,
  });
}

// Creating/renaming/recoloring/deleting a label changes the label set itself, so
// everything that renders one has to be refetched.
function useInvalidateLabels(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: LABELS_KEY });
    void queryClient.invalidateQueries({ queryKey: LABEL_USAGE_KEY });
    void queryClient.invalidateQueries({ queryKey: ["label-counts"] });
    void queryClient.invalidateQueries({ queryKey: ["pipeline"] });
    void queryClient.invalidateQueries({ queryKey: ["leads"] });
    void queryClient.invalidateQueries({ queryKey: ["lead"] });
  };
}

// The board and lead-detail payloads both carry `labels` on the lead.
type LabelledLead = { id: string; labels: LeadLabel[] };

/**
 * Rewrites one lead's labels in every cache that holds it, without refetching.
 *
 * (Un)assigning used to invalidate `["pipeline"]`, which re-fetched the entire
 * board — 478 leads on the pilot — and re-rendered every card, for a change that
 * affects exactly one lead. The label set itself is untouched by an assignment,
 * so `["labels"]` is not invalidated either; only the per-label counts are.
 */
function usePatchLeadLabels(): (
  leadId: string,
  apply: (labels: LeadLabel[]) => LeadLabel[],
) => void {
  const queryClient = useQueryClient();
  return (leadId, apply) => {
    queryClient.setQueriesData<{ items: LabelledLead[] }>(
      { queryKey: ["pipeline"] },
      (old) =>
        old && {
          ...old,
          items: old.items.map((l) =>
            l.id === leadId ? { ...l, labels: apply(l.labels) } : l,
          ),
        },
    );
    queryClient.setQueryData<LabelledLead>(
      ["lead", leadId],
      (old) => old && { ...old, labels: apply(old.labels) },
    );
    void queryClient.invalidateQueries({ queryKey: ["label-counts"] });
    // An assignment is exactly what the modal's delete guard reads. The key is
    // inactive while the modal is closed, so this marks it stale for the next
    // open without firing a request now.
    void queryClient.invalidateQueries({ queryKey: LABEL_USAGE_KEY });
  };
}

export function useCreateLabel(): ReturnType<
  typeof useMutation<LeadLabel, Error, CreateLeadLabelInput>
> {
  const invalidate = useInvalidateLabels();
  return useMutation<LeadLabel, Error, CreateLeadLabelInput>({
    mutationFn: createLabelApi,
    onSuccess: () => {
      toast.success("Etiqueta creada.");
      invalidate();
    },
    onError: (error) => toast.error(error.message ?? "Error al crear la etiqueta."),
  });
}

type UpdateArgs = { id: string; data: UpdateLeadLabelInput };

export function useUpdateLabel(): ReturnType<
  typeof useMutation<LeadLabel, Error, UpdateArgs>
> {
  const invalidate = useInvalidateLabels();
  return useMutation<LeadLabel, Error, UpdateArgs>({
    mutationFn: ({ id, data }) => updateLabelApi(id, data),
    onSuccess: () => {
      toast.success("Etiqueta actualizada.");
      invalidate();
    },
    onError: (error) => toast.error(error.message ?? "Error al actualizar la etiqueta."),
  });
}

export function useDeleteLabel(): ReturnType<
  typeof useMutation<void, Error, string>
> {
  const invalidate = useInvalidateLabels();
  return useMutation<void, Error, string>({
    mutationFn: deleteLabelApi,
    onSuccess: () => {
      toast.success("Etiqueta eliminada.");
      invalidate();
    },
    onError: (error) => toast.error(error.message ?? "Error al eliminar la etiqueta."),
  });
}

type AssignArgs = { leadId: string; labelId: string };

export function useAssignLabel(): ReturnType<
  typeof useMutation<void, Error, AssignArgs>
> {
  const patch = usePatchLeadLabels();
  const queryClient = useQueryClient();
  return useMutation<void, Error, AssignArgs>({
    mutationFn: ({ leadId, labelId }) => assignLabelApi(leadId, labelId),
    onSuccess: (_data, { leadId, labelId }) => {
      // The assign endpoint returns 204, so the label body comes from the cache.
      const label = (queryClient.getQueryData<LeadLabel[]>(LABELS_KEY) ?? []).find(
        (l) => l.id === labelId,
      );
      if (!label) return;
      patch(leadId, (labels) =>
        labels.some((l) => l.id === labelId) ? labels : [...labels, label],
      );
    },
    onError: (error) => toast.error(error.message ?? "Error al asignar la etiqueta."),
  });
}

export function useUnassignLabel(): ReturnType<
  typeof useMutation<void, Error, AssignArgs>
> {
  const patch = usePatchLeadLabels();
  return useMutation<void, Error, AssignArgs>({
    mutationFn: ({ leadId, labelId }) => unassignLabelApi(leadId, labelId),
    onSuccess: (_data, { leadId, labelId }) =>
      patch(leadId, (labels) => labels.filter((l) => l.id !== labelId)),
    onError: (error) => toast.error(error.message ?? "Error al quitar la etiqueta."),
  });
}
